import { z } from "zod";

import type { MarketUserPermissionsSubSubscription } from "../../__generated__/gql/graphql";
import { parseRoles } from "../../utils/roles";

export const subscriptionTier = z.enum([
  "artis_lite",
  "artis_enhanced_noexch",
  "artis_enhanced",
  "artis_professional",
  "artis_internal",
]);

export type SubscriptionTier = z.infer<typeof subscriptionTier>;

/**
 * Tier numeric values for easier comparison.
 * Higher number = higher tier with more privileges.
 */
export const tierToNumber: Record<SubscriptionTier, number> = {
  artis_lite: 1,
  artis_enhanced_noexch: 2,
  artis_enhanced: 2,
  artis_professional: 3,
  artis_internal: 4,
};

/**
 * Entitlement levels from `user_entitlement_level`, mapped onto the tiers the
 * app gates on. `artis_enhanced_noexch` is level 15 — below enhanced, above
 * lite — so a user holding both grants resolves to full enhanced.
 */
const LEVEL_TIER: Record<number, SubscriptionTier> = {
  10: "artis_lite",
  15: "artis_enhanced_noexch",
  20: "artis_enhanced",
  30: "artis_professional",
  40: "artis_internal",
};

/**
 * The tier the app gates on.
 *
 * A live entitlement grant wins. Level 0 ("none") means no grant covers right
 * now — which, while entitlements are still being backfilled, is the normal
 * state for most users, so it falls back to the legacy
 * `folio_user.subscription_tier` column rather than locking them out. Once
 * every user has a grant, the fallback becomes dead code and the column can go.
 *
 * Returns undefined when neither source yields a tier the app recognises;
 * callers treat that as no access.
 */
export const resolveTier = (
  entitlementLevel: number | null | undefined,
  legacyTier: string | null | undefined,
): SubscriptionTier | undefined => {
  if (entitlementLevel != null && entitlementLevel > 0) {
    const fromGrant = LEVEL_TIER[entitlementLevel];
    // an unmapped non-zero level is a tier this build predates — fall through
    // to the legacy column rather than silently treating it as no access
    if (fromGrant) return fromGrant;
  }
  const parsed = subscriptionTier.safeParse(legacyTier);
  return parsed.success ? parsed.data : undefined;
};

/**
 * Check if a tier has at least the privileges of the minimum required tier.
 * e.g., hasTierAtLeast("artis_professional", "artis_enhanced") returns true
 */
export function hasTierAtLeast(
  userTier: SubscriptionTier | string | null | undefined,
  minimumTier: SubscriptionTier,
): boolean {
  if (!userTier) return false;
  const parsed = subscriptionTier.safeParse(userTier);
  if (!parsed.success) return false;
  return tierToNumber[parsed.data] >= tierToNumber[minimumTier];
}

/**
 * @deprecated `artis_internal` describes an Artis employee, not a commercial
 * arrangement, so it is being replaced by the `staff` role — use {@link isStaff}.
 * Still checked alongside the role while existing internal users are migrated;
 * docs/subscription-tier-removal.md lists every call site to drop.
 */
export function isInternalTier(tier: string | null | undefined): boolean {
  return tier === subscriptionTier.enum.artis_internal;
}

/** Artis employee. Held on `folio_user.roles`, assigned from the UMI user page. */
export const STAFF_ROLE = "staff";

/**
 * Whether a user's roles mark them as Artis staff.
 *
 * Takes the raw `roles` value rather than a parsed set so the pure gate
 * functions can be called with a user row straight from the query.
 */
export const isStaff = (roles: unknown): boolean => parseRoles(roles).has(STAFF_ROLE);

/**
 * Stable identity of a user's folder permission rows: level and folder only, so
 * folder or curve renames carried by the same rows do not look like a change.
 */
export const permissionRowsFingerprint = (
  rows: MarketUserPermissionsSubSubscription["permission"] | null | undefined,
) =>
  (rows ?? [])
    .map((row) => `${row.id}:${row.package}:${row.permission}`)
    .sort()
    .join("|");
